/* eslint-disable max-lines */
import {
    CloseOutlined,
    DownloadOutlined,
    LeftOutlined,
    RightOutlined,
} from '@ant-design/icons';
import DocViewer, { IDocument } from '@cyntler/react-doc-viewer';
import { Button } from 'antd';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useState } from 'react';
import { AnyFileRenderer } from 'src/components/Preview/AnyFileRenderer';
import { ImageRenderer } from 'src/components/Preview/ImageRenderer';
import { PDFRenderer } from 'src/components/Preview/PDFRenderer';
import { PreviewController } from 'src/components/Preview/PreviewController';
import { UrlHelper } from 'src/utils/UrlHelper';
import styled from 'styled-components';

const wrap = (min: number, max: number, index: number) => {
    if (min > index) {
        return min;
    }

    if (max < index) {
        return max;
    }

    return index;
};

const SLIDE_OFFSET_PX = 1000;

/**
 * Slides the current document in from the side the user paginated from.
 * Implemented with a plain CSS transition rather than framer-motion: the version
 * pinned here (4.x) does not support React 19 and silently never runs, which left
 * the document parked off-screen at opacity 0 (LBOR-69).
 */
const Slide = ({
    page,
    direction,
    children,
}: {
    page: number;
    direction: number;
    children: ReactNode;
}) => {
    const [entered, setEntered] = useState(false);
    const enterOffset = direction > 0 ? SLIDE_OFFSET_PX : -SLIDE_OFFSET_PX;

    useEffect(() => {
        setEntered(false);

        const frame = requestAnimationFrame(() => setEntered(true));

        return () => cancelAnimationFrame(frame);
    }, [page]);

    return (
        <SlideWrapper $offset={entered ? 0 : enterOffset} $visible={entered}>
            {children}
        </SlideWrapper>
    );
};

type PreviewProps = {
    hideNavigateButtons?: boolean;
};

export const Preview = observer(({ hideNavigateButtons }: PreviewProps) => {
    const [[page, direction], setPage] = useState([
        PreviewController.startIndex,
        0,
    ]);

    useEffect(() => {
        setPage([PreviewController.startIndex, 0]);
    }, [PreviewController.startIndex]);

    const imageIndex = wrap(0, PreviewController.documentsCount - 1, page);

    const paginate = (newDirection: number) => {
        const newPage = page + newDirection;
        if (newPage > -1 && newPage < PreviewController.documentsCount) {
            setPage([page + newDirection, newDirection]);
        }
    };

    const source = PreviewController.documents?.[imageIndex];

    if (!PreviewController.isOpen || !source) {
        return null;
    }

    return (
        <PreviewWrapper className="file-preview">
            <PreviewWrapperHeader className="file-preview__header">
                <HeaderFileName>{source.originalName}</HeaderFileName>
                <div>
                    <StyledButton
                        onClick={(e) => {
                            const name = source.originalName || source.name;
                            if (source.uri && name) {
                                UrlHelper.downloadFile(source.uri, name);
                            }

                            (e.target as HTMLButtonElement)?.blur();
                        }}
                    >
                        <DownloadOutlined />
                    </StyledButton>
                    <StyledButton
                        onClick={() => {
                            PreviewController.close();
                        }}
                    >
                        <CloseOutlined />
                    </StyledButton>
                </div>
            </PreviewWrapperHeader>
            <PreviewWrapperBody className="file-preview__body">
                <Slide key={page} page={page} direction={direction}>
                    <StyledDocViewer
                        pluginRenderers={[PDFRenderer, ImageRenderer]}
                        documents={[source as IDocument]}
                        config={{
                            header: {
                                disableHeader: false,
                                disableFileName: false,
                                retainURLParams: false,
                            },
                            noRenderer: {
                                overrideComponent: AnyFileRenderer,
                            },
                        }}
                    />
                </Slide>
            </PreviewWrapperBody>
            {!hideNavigateButtons && (
                <>
                    <div
                        className="file-preview-nav-btn next"
                        onClick={() => paginate(1)}
                    >
                        <RightOutlined />
                    </div>
                    <div
                        className="file-preview-nav-btn prev"
                        onClick={() => paginate(-1)}
                    >
                        <LeftOutlined />
                    </div>
                </>
            )}
        </PreviewWrapper>
    );
});

const SlideWrapper = styled.div<{ $offset: number; $visible: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    transform: translateX(${({ $offset }) => $offset}px);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
        opacity 0.2s linear;
`;

const StyledDocViewer = styled(DocViewer)`
    &#react-doc-viewer {
        margin: 0 auto;
        width: 100%;
        background: transparent;
        display: block;
        overflow: auto;
    }

    #image-renderer {
        background: transparent;
    }

    > div:first-child {
        margin: 0 auto;
    }

    #header-bar {
        display: none;
    }

    #proxy-renderer {
        width: 100%;
        color: white;
        display: flex;
        align-items: center;
        height: 100%;
        overflow: hidden;
        position: relative;
    }

    #no-renderer-download {
        height: 44px;
        padding: 9px 12px;
        border-radius: 8px;
        min-width: 147px;
        background-color: $blue;

        &:focus,
        &:hover {
            background-color: darken($blue, 10%);
        }
    }

    #no-renderer {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
    }

    #pdf-renderer {
        background: white;
    }
`;

const PreviewWrapper = styled.div`
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    background: #1e1e1ed9;
    z-index: 1000;
    display: block;
    flex-direction: column;

    .file-preview-nav-btn {
        position: absolute;
        width: 50px;
        height: 50px;
        font-size: 32px;
        color: white;

        display: flex;
        justify-content: center;
        align-items: center;
        background: #31313199;
        z-index: 1;
        cursor: pointer;

        &:hover: {
            background: #313131db;
        }
    }

    .prev {
        left: 0;
        top: calc(50% - 25px);
    }

    .next {
        right: 0;
        top: calc(50% - 25px);
    }
`;

const PreviewWrapperHeader = styled.div`
    position: sticky;
    top: 0;
    display: flex;
    height: fit-content;
    z-index: 2;
    background-image: linear-gradient(180deg, #000000e3, transparent);
    gap: 16px;
    align-items: center;
`;

const PreviewWrapperBody = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    overflow-y: hidden;
    width: 100%;
    height: 100%;
`;

const StyledButton = styled(Button)`
    border-radius: 0;
    background: none;
    border: none;
    color: white;
    font-size: 22px;
    height: fit-content;
    line-height: 1;

    &:hover,
    &:active,
    &:focus {
        background: #31313199;
        color: white;
    }
`;

export const HeaderFileName = styled.div`
    flex: 1;
    color: white;
    padding: 0 16px;
    font-size: 13px;
`;
